import { useEffect, useRef, useState } from 'react'
import { Icon } from 'assets/index'
import { useDropzone } from 'react-dropzone'
import {
  FileInput,
  TextInput,
  Subtitle,
  Underlined,
  GreyInfo,
  Row,
  Column,
  Flag,
  ActionIcon,
  Ancor,
  FileImage,
  InputError,
} from './style'

interface StyleOptions {
  icon: string // icone de pdf ou imagem
  accept: string
  label: string // Titulo do input (ex: Upload de PDF)
  info: string // Recomendacao de upload (ex: • Documento no formato PDF)
}

interface UploadProps {
  id?: string
  mandatory?: boolean // quando o input e obrigatorio
  options: StyleOptions
  ready?: boolean // para ficar na configuracao de border vermelha
  file?: any // Recebe o arquivo para consulta
  disableInput?: boolean
  fileName?: string // caso o arquivo ja tenha titulo
  nameFilePlaceholder?: string // caso precise preencher titulo
  edit?: boolean
  download?: boolean
  visualize?: boolean
  flag?: string
  onChangeFile?: (_file: File) => any // callback ao subir arquivo
  onClickCheck?: any // callback para icone de check
  onClickTrash?: any // callback para icone de lixo
  onClickRemoveFile: any // callback para excluir file selecionado
  onChangeFileName?: any
  erro?: string
  onFocus?: () => void
  url?: string | undefined
}

const status: any = {
  PENDENT: 'Pendente',
  SIGNED: 'Assinado',
}

const UploadFile = (props: UploadProps) => {
  const textRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | undefined>(props?.fileName)
  const [pdfLink, setPdfLink] = useState<string | undefined>(props?.url)
  const [file, setFile] = useState<any>(props?.file)
  const { onChangeFile, onChangeFileName } = props

  useEffect(() => {
    props?.file && setFile(props?.file)
  }, [props?.file])

  useEffect(() => {
    if (!file) return
    if (props?.download || props?.visualize) {
      if (props?.url) {
        return setPdfLink(props?.url)
      }
      if (props?.file) {
        setPdfLink(props?.file?.url)
        return
      }
      const url = URL.createObjectURL(file)
      setPdfLink(url)
    }
  }, [file])

  const onDrop = (acceptedFiles: any) => {
    setFile(acceptedFiles[0])
    onChangeFile && onChangeFile(acceptedFiles[0])
  }

  const transformExtensionsToAccept = (
    extensions: string | string[]
  ): Record<string, string[]> => {
    // Define um mapeamento de extensões para tipos MIME
    const mimeTypesMap: Record<string, string> = {
      '.pdf': 'application/pdf',
      '.png': 'image/png',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.txt': 'text/plain',
      '.csv': 'text/csv',
    }

    // Garante que extensions seja um array
    const extensionArray =
      typeof extensions === 'string'
        ? extensions.split(',').map((ext) => ext.trim())
        : extensions

    // Objeto final para armazenar o resultado
    const accept: Record<string, string[]> = {}

    // Itera sobre as extensões e constrói o objeto
    extensionArray.forEach((ext) => {
      const mimeType = mimeTypesMap[ext]
      if (mimeType) {
        if (!accept[mimeType]) {
          accept[mimeType] = []
        }
        accept[mimeType].push(ext)
      }
    })

    return accept
  }

  const { getRootProps, getInputProps } = useDropzone({
    onDrop, // função q é chamada quando é dropado arquivos
    accept: transformExtensionsToAccept(props?.options.accept),
    multiple: false, // permite somente um arquivo
  }) // props da Dropzone

  return (
    <Column id={props?.id}>
      <Row style={{ width: '100%' }}>
        <TextInput
          ref={textRef}
          type="text"
          maxLength={24}
          placeholder={props?.nameFilePlaceholder}
          disabled={!props?.edit}
          defaultValue={fileName}
          onChange={(e) => {
            setFileName(e.target.value)
            onChangeFileName && onChangeFileName(e.target.value)
          }}
        />
        {props?.onClickCheck && (
          <ActionIcon
            alt="check-icon"
            src={Icon('check_circle.svg')}
            onClick={() => props?.onClickCheck(fileName, file)}
          />
        )}
        {props?.edit && (
          <ActionIcon
            alt="edit-icon"
            src={Icon('edit.svg')}
            onClick={() => textRef.current?.focus()}
          />
        )}
        {props?.onClickTrash && (
          <ActionIcon
            alt="trash-icon"
            src={Icon('trash.svg')}
            onClick={() => props?.onClickTrash(fileName, props?.id)}
          />
        )}
        {file && props?.visualize && (
          <ActionIcon
            alt="doc-icon"
            src={Icon('doc.svg')}
            onClick={() => {
              window.open(pdfLink, '_blank')
            }}
          />
        )}
        {props?.download && (
          <a href={pdfLink} download={file?.name}>
            <ActionIcon
              alt="download-icon"
              src={Icon('login.svg')}
              style={{ transform: 'rotate(270deg)' }}
            />
          </a>
        )}
        {props?.flag && <Flag flag={props?.flag}>{status[props?.flag]}</Flag>}
      </Row>
      <div
        {...getRootProps({
          onClick: (event) => {
            event.preventDefault() // Evita comportamento padrão ao clicar na área
          },
        })}
      >
        <input
          {...getInputProps()}
          accept={Object.keys(props?.options.accept || {})
            .map((mimeType) => mimeType)
            .join(',')}
          disabled={props?.disableInput}
        />
        <Row style={{ marginTop: 16 }}>
          <Underlined>Selecione um arquivo</Underlined>
          <GreyInfo>{` ou arraste e solte aqui`}</GreyInfo>
        </Row>
      </div>
      <Ancor>
        <div
          {...getRootProps({
            onClick: (event) => {
              event.preventDefault() // Evita comportamento padrão ao clicar na área
            },
          })}
        >
          <input
            {...getInputProps()}
            accept={Object.keys(props?.options.accept || {})
              .map((mimeType) => mimeType)
              .join(',')}
            disabled={props?.disableInput}
          />
          <FileInput
            error={props?.erro}
            htmlFor={fileName}
            filled={file}
            ready={props?.ready}
            mandatory={props?.mandatory}
          >
            <Row>
              <FileImage
                mandatory={props?.mandatory}
                filled={file}
                ready={props?.ready}
                className="xablau"
                alt="file-icon"
                src={Icon(props?.options.icon)}
                style={{ marginRight: 15 }}
              />
              <span id="upload-text">{file?.name || props?.options.label}</span>
            </Row>
            {!file && (
              <FileImage
                mandatory={props?.mandatory}
                ready={props?.ready}
                alt="file-icon"
                src={Icon('cloud.svg')}
                style={{ right: 16 }}
              />
            )}
          </FileInput>
        </div>
        {props?.erro && <InputError>{props?.erro}</InputError>}
        {file && !props?.disableInput && (
          <FileImage
            filled
            alt="file-icon"
            src={Icon('x_circle.svg')}
            style={{ right: 16, top: 12 }}
            onClick={() => {
              props?.onClickRemoveFile(fileName)
              setFile(null)
            }}
          />
        )}
      </Ancor>
      <Subtitle>Recomendações do upload:</Subtitle>
      <Column>
        <GreyInfo small>{props?.options.info}</GreyInfo>
        <GreyInfo small>• Tamanho máximo do arquivo: 10MB</GreyInfo>
      </Column>
    </Column>
  )
}

export default UploadFile
